    //  w-3/4 sm:w-[350px] md:w-[380px] xl:w-[400px] 2xl:w-[500px]

    import React, { useCallback, useEffect, useRef, useState } from "react";
    import * as d3 from "d3";
    
    const Sunburst = ({ width, height, sunburst_data }) => {
      const chartRef = useRef(null);
      const breadcrumbRef = useRef(null);
      const [percentage, setPercentage] = useState("");
    
      const b = {
        w: width / 4, // Width of each breadcrumb, relative to container width
        h: height / 12, // Height of each breadcrumb, relative to container height
        s: 5, // Spacing between breadcrumbs
        t: 10, // Tip length for the arrow effect
      };
    
      const radius = Math.min(width, height) / 2;
    
      const createVisualization = useCallback(() => {
        const svg = d3
          .select(chartRef.current)
          .append("svg")
          .attr("viewBox", `0 0 ${width} ${height}`)
          .attr("width", "100%")
          .attr("height", "100%")
          .append("g")
          .attr("transform", `translate(${width / 2}, ${height / 2})`);
    
        const partition = d3.partition().size([2 * Math.PI, radius * radius]);
    
        const root = d3
          .hierarchy(sunburst_data)
          .sum((d) => d.value)
          .sort((a, b) => b.value - a.value);
    
        const arc = d3
          .arc()
          .startAngle((d) => d.x0)
          .endAngle((d) => d.x1)
          .innerRadius((d) => Math.sqrt(d.y0))
          .outerRadius((d) => Math.sqrt(d.y1));
    
        const nodes = partition(root).descendants();
    
        svg
          .selectAll("path")
          .data(nodes)
          .enter()
          .append("path")
          .attr("d", arc)
          .style("stroke", "#ffffff") 
          .style("strokeWidth", 0.5) 
          .style("fill", (d) => d.data.color)
          .style("opacity", 1)
          .on("mouseover", mouseover);
    
        const totalSize = root.value;
    
        function mouseover(event, d) {
          if (d.depth === 0) return;
          const percentage = ((100 * d.value) / totalSize).toPrecision(3) + "%";
          setPercentage(percentage);
    
          const sequenceArray = d.ancestors().reverse().slice(1);
          d3.select(breadcrumbRef.current).selectAll("g").remove();
          updateBreadcrumbs(sequenceArray, percentage);
    
          svg
            .selectAll("path")
            .style("opacity", (node) => (sequenceArray.includes(node) ? 1 : 0.3));
    
          d3.select(breadcrumbRef.current).style("visibility", "visible");
          d3.select(".households-text").style("visibility", "visible");
        }
    
        svg.on("mouseleave", mouseleave);
        initializeBreadcrumbTrail();
      }, [sunburst_data, width, height, radius]);

      useEffect(() => {
        if (sunburst_data && chartRef.current && breadcrumbRef.current) {
          d3.select(chartRef.current).selectAll("*").remove();
          d3.select(breadcrumbRef.current).selectAll("*").remove();
          createVisualization();
        }
      }, [sunburst_data, createVisualization]);

      const mouseleave = () => {
        d3.selectAll("path").style("opacity", 1);
        d3.select(breadcrumbRef.current).style("visibility", "hidden");
        setPercentage("");
        d3.select(".households-text").style("visibility", "hidden");
      };
    
      const breadcrumbPoints = (d, i) => {
        const points = [
          `0,0`,
          `${b.w},0`,
          `${b.w + b.t},${b.h / 2}`,
          `${b.w},${b.h}`,
          `0,${b.h}`,
        ];
        if (i > 0) points.push(`${b.t},${b.h / 2}`);
        return points.join(" ");
      };
    
      const initializeBreadcrumbTrail = () => {
        const trail = d3
          .select(breadcrumbRef.current)
          .append("svg")
          .attr("width", "100%")
          .attr("height", b.h)
          .attr("viewBox", `0 0 ${width} ${height / 12}`)
          .attr("id", "breadcrumb-trail");
    
        trail
          .append("text")
          .attr("id", "endlabel")
          .style("fill", "#000")
          .style("font-size", `${b.h / 2}px`)
          .style("text-anchor", "middle");
        
      };
    
      const updateBreadcrumbs = (nodeArray, percentageString) => {
        const trail = d3.select(breadcrumbRef.current).select("#breadcrumb-trail");
    
        trail.selectAll("g").remove();
    
        const breadcrumb = trail
          .selectAll("g")
          .data(nodeArray, (d) => nodeArray.map((n) => n.data.name).join("-"));
    
        const entering = breadcrumb.enter().append("g");
    
        entering
          .append("polygon")
          .attr("points", breadcrumbPoints)
          .style("fill", (d) => d.data.color);
    
          entering
          .append("text")
          .attr("x", (b.w + b.t) / 2)
          .attr("y", b.h / 2)
          .style("fill", "#fff")
          .attr("text-anchor", "middle")
          .style("font-size", `${b.h / 2}px`)
          .each(function(d) {
            const text = d3.select(this);
            const words = d.data.name.split(/\s+/);
            let line = [];
           
            const lineHeight = 1.1; // Line height in em units
            const x = (b.w + b.t) / 2;
            const dy = 0.35;
            
            // Temporary tspan to calculate line count for centering
            let tempTspan = text.append("tspan").attr("x", x).attr("dy", dy + "em");
            let lineCount = 0;
        
            // Calculate number of lines by wrapping text into tspans
            words.forEach((word) => {
              line.push(word);
              tempTspan.text(line.join(" "));
              if (tempTspan.node().getComputedTextLength() > b.w - 10) {
                line.pop();
                tempTspan.text(line.join(" "));
                line = [word];
                lineCount++;
                tempTspan = text.append("tspan").attr("x", x).attr("dy", lineCount * lineHeight + dy + "em").text(word);
              }
            });
            if (line.length) lineCount++; // Include the last line
        
            // Remove temporary tspans and start actual rendering with center alignment
            text.selectAll("tspan").remove();
            let verticalOffset = (-((lineCount - 1) * lineHeight) / 2) + dy;
        
            // Render tspans with adjusted vertical alignment
            let tspan = text.append("tspan").attr("x", x).attr("dy", verticalOffset + "em");
            line = [];
            words.forEach((word) => {
              line.push(word);
              tspan.text(line.join(" "));
              if (tspan.node().getComputedTextLength() > b.w - 10) {
                line.pop();
                tspan.text(line.join(" "));
                line = [word];
                tspan = text.append("tspan").attr("x", x).attr("dy", lineHeight + "em").text(word);
              }
            });
          });
        
    
        breadcrumb
          .merge(entering)
          .attr("transform", (d, i) => `translate(${i * (b.w + b.s)}, 0)`);
    
        trail
          .select("#endlabel")
          .attr("x", (nodeArray.length + 0.3) * (b.w + b.s))
          .attr("y", b.h / 1.5)
          .text(percentageString);
    
        trail.style("opacity", 1);
      };
    
      return (
        <>
          {sunburst_data &&
          sunburst_data.children &&
          sunburst_data.children.length > 0 ? (
            <div className="font-sans font-normal mt-2.5 relative ">
              <div className="absolute top-[40%] sm:top-[45%] left-[50%] transform -translate-x-1/2 
              -translate-y-1/2 text-center text-gray-500">
                <span className="text-xl  sm:text-2xl md:text-4xl font-bold">
                  {percentage}
                </span>
                <br />
                <span
                  className={`${
                    percentage ? "block text-[10px] sm:text-sm " : "hidden"
                  } households-text `}
                >
                  of Households
                </span>
              </div>
              <div className="" ref={chartRef}></div>
              <div ref={breadcrumbRef} className="mt-5 flex flex-wrap lg:flex-nowrap justify-end pr-4"></div>
            </div>
          ) : (
           <div className="my-3">
             <p className="text-left text-lg text-gray-500 font-semibold ">No data Found</p>
           </div>
          )}
        </>
      );
    };
    
    export default Sunburst;
    